if (!customElements.get('lazy-video-reels')) {
  customElements.define(
    'lazy-video-reels',
    class LazyVideoReels extends HTMLElement {
      connectedCallback() {
        this.sectionWrapper = this.closest('.motion-reels');
        this.cards = Array.from(this.querySelectorAll('.motion-reels__card'));
        this.isVisible = false;
        this.boundOnVisibilityChange = this.onVisibilityChange.bind(this);
        this.rootMargin = this.dataset.rootMargin || '100px';

        if (!this.cards.length) return;

        // 1. Observer for the section itself to enable/disable marquee animation
        this.sectionObserver = new IntersectionObserver(this.onSectionIntersection.bind(this), {
          root: null,
          rootMargin: '100px',
          threshold: 0.05,
        });
        this.sectionObserver.observe(this);

        // 2. Observer for individual cards to lazy-load src and play/pause
        this.videoObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const video = entry.target.querySelector('.motion-reels__video');
            if (!video) return;

            if (entry.isIntersecting && this.isVisible && !document.hidden) {
              this.loadAndPlay(video);
            } else {
              video.pause();
            }
          });
        }, {
          root: null,
          rootMargin: '100px', // start loading/playing 100px before entering viewport
          threshold: 0.01
        });

        // We only start observing cards AFTER the page load event has fired,
        // to prevent video loads from blocking the initial page load speed!
        if (document.readyState === 'complete') {
          this.startObservingCards();
        } else {
          window.addEventListener('load', () => this.startObservingCards());
        }

        document.addEventListener('visibilitychange', this.boundOnVisibilityChange);
      }

      startObservingCards() {
        this.cards.forEach((card) => this.videoObserver.observe(card));
      }

      disconnectedCallback() {
        this.sectionObserver?.disconnect();
        this.videoObserver?.disconnect();
        document.removeEventListener('visibilitychange', this.boundOnVisibilityChange);
      }

      onSectionIntersection(entries) {
        const entry = entries[0];
        this.isVisible = Boolean(entry?.isIntersecting);

        if (this.isVisible && !document.hidden) {
          this.sectionWrapper?.classList.add('is-visible');
          this.playVisibleVideos();
        } else {
          this.sectionWrapper?.classList.remove('is-visible');
          this.pauseAll();
        }
      }

      onVisibilityChange() {
        if (document.hidden) {
          this.pauseAll();
          this.sectionWrapper?.classList.remove('is-visible');
        } else if (this.isVisible) {
          this.sectionWrapper?.classList.add('is-visible');
          this.playVisibleVideos();
        }
      }

      playVisibleVideos() {
        this.cards.forEach((card) => {
          const video = card.querySelector('.motion-reels__video');
          if (!video) return;

          const rect = card.getBoundingClientRect();
          const inViewport = (
            rect.top < window.innerHeight + 100 &&
            rect.bottom > -100 &&
            rect.left < window.innerWidth + 100 &&
            rect.right > -100
          );

          if (inViewport && !document.hidden) {
            this.loadAndPlay(video);
          } else {
            video.pause();
          }
        });
      }

      loadAndPlay(video) {
        // Set src from data-src only when needed (lazy load)
        if (!video.src && video.dataset.src) {
          video.src = video.dataset.src;
          video.load();
        }

        const playPromise = video.play();
        if (playPromise?.catch) {
          playPromise.catch(() => {});
        }
      }

      pauseAll() {
        this.cards.forEach((card) => {
          const video = card.querySelector('.motion-reels__video');
          video?.pause();
        });
      }
    }
  );
}
